import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Drawer,
  Button,
} from '@mui/material';
import { getImageSrc } from '../../utils/imageHelper';
import { getColorFromName } from '../../utils/colorUtils';
import { BUTTON_TEXT_SX, COLORS, FONTS } from '../../theme/tokens';

const isColorFilter = (title) => {
  const lower = title.toLowerCase();
  return lower.includes('color') || lower.includes('colour');
};

const normalizeFilterKeys = (filters) => {
  const normalized = {};
  Object.entries(filters || {}).forEach(([key, values]) => {
    normalized[String(key)] = [...values];
  });
  return normalized;
};

const FilterBottomSheet = ({
  open,
  onClose,
  filters,
  selectedFilters,
  onApply,
  resultCount,
}) => {
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState({});
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setDraftFilters(normalizeFilterKeys(selectedFilters));
    }
    prevOpenRef.current = open;
  }, [open, selectedFilters]);

  const toggleFilter = (parentId, childId) => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      const parentKey = String(parentId);

      if (!next[parentKey]) {
        next[parentKey] = [];
      }

      if (next[parentKey].includes(childId)) {
        next[parentKey] = next[parentKey].filter((id) => id !== childId);
        if (next[parentKey].length === 0) {
          delete next[parentKey];
        }
      } else {
        next[parentKey] = [...next[parentKey], childId];
      }

      return next;
    });
  };

  const handleClearAll = () => {
    setDraftFilters({});
  };

  const handleApply = () => {
    onApply(draftFilters);
  };

  if (!filters.length) {
    return null;
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px 12px 0 0',
          p: '14px 20px 20px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        data-testid="filter-sheet-grabber"
        sx={{
          width: 40,
          height: 4,
          bgcolor: COLORS.borderControl,
          borderRadius: 2,
          mx: 'auto',
          mb: 2,
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: FONTS.heading,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: COLORS.textPrimary,
          }}
        >
          {t('shop.filters')}
        </Typography>
        <Button
          variant="text"
          onClick={handleClearAll}
          sx={{
            minWidth: 'auto',
            p: 0,
            textTransform: 'none',
            fontSize: '0.8125rem',
            color: COLORS.textSecondary,
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'underline',
              bgcolor: 'transparent',
            },
          }}
        >
          {t('shop.clearAll')}
        </Button>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>
        {filters.map((filter) => {
          const colorFilter = isColorFilter(filter.title);

          return (
            <Box
              key={filter.id}
              sx={{
                borderBottom: `1px solid ${COLORS.borderHairline}`,
                pb: '20px',
                mb: '20px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: COLORS.textPrimary,
                  mb: 2,
                }}
              >
                {filter.title}
              </Typography>

              {colorFilter ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '14px',
                  }}
                >
                  {filter.children.map((child) => {
                    const isSelected = draftFilters[String(filter.id)]?.includes(child.attribute_id) || false;

                    return (
                      <Box
                        key={child.attribute_id}
                        component="button"
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={child.displayTitle}
                        onClick={() => toggleFilter(filter.id, child.attribute_id)}
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          border: 'none',
                          p: 0,
                          cursor: 'pointer',
                          backgroundImage: child.attribute_image
                            ? `url(${getImageSrc(child.attribute_image, 'thumbs')})`
                            : 'none',
                          backgroundColor: child.attribute_image
                            ? 'transparent'
                            : getColorFromName(child.displayTitle),
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          boxShadow: isSelected
                            ? `0 0 0 2px #FFF, 0 0 0 3.5px ${COLORS.green}`
                            : `0 0 0 1px ${COLORS.borderControl}`,
                        }}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  {filter.children.map((child) => {
                    const isSelected = draftFilters[String(filter.id)]?.includes(child.attribute_id) || false;

                    return (
                      <Box
                        key={child.attribute_id}
                        component="button"
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={child.displayTitle}
                        onClick={() => toggleFilter(filter.id, child.attribute_id)}
                        sx={{
                          p: '10px 18px',
                          borderRadius: '999px',
                          border: isSelected
                            ? `1.5px solid ${COLORS.green}`
                            : `1px solid ${COLORS.borderControl}`,
                          bgcolor: isSelected ? 'rgba(58,79,65,0.05)' : 'transparent',
                          color: isSelected ? COLORS.green : COLORS.textBody,
                          fontFamily: FONTS.body,
                          fontSize: '0.8125rem',
                          fontWeight: isSelected ? 600 : 400,
                          cursor: 'pointer',
                        }}
                      >
                        {child.displayTitle}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          pt: 1,
          bgcolor: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={handleApply}
          sx={{
            ...BUTTON_TEXT_SX,
            height: 52,
            bgcolor: COLORS.green,
            color: '#FFFFFF',
            borderRadius: '2px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: COLORS.greenDark,
              boxShadow: 'none',
            },
          }}
        >
          {`Show ${resultCount} products`}
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterBottomSheet;
